/* eslint-disable react-refresh/only-export-components */
import { createContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  ENV_TYPES,
  SUN_STATE,
  MODELS,
  CAMERA_PREFIX_NAMES,
  EXTERIOR_CAMERA_NAMES,
  EXTERIOR_CAMERA_LABELS,
  STREAM_TYPES,
  ANIMATIONS,
} from "shared/enum";
import useModel from "hooks/useModel";
import useConfigurationLoading from "hooks/useConfigurationLoading";
import { fetchDataFile } from "helpers/requests";

import type {
  StreamingState,
  TurntableState,
  DemoState,
  ApiModelVariantSet,
  Camera,
  Section,
  Scenario,
  StreamTypes,
} from "types/app";
import type {
  apimodel_Paginated_apimodel_Camera,
  apimodel_Paginated_apimodel_Scenario,
} from "@3ds/3DEXCITEshow-streaming";
import type { Dispatch, SetStateAction } from "react";

type StreamContextType = {
  loaded: boolean;
  loadError: boolean;
  cameras: Camera[];
  scenarios: Scenario[];
  scenarioVariants: undefined[] | ApiModelVariantSet;
  animations: [];
  streamViewType: StreamTypes;
  demoState: DemoState;
  streamingState: StreamingState;
  turntableState: TurntableState;
  setStreamViewType: Dispatch<SetStateAction<StreamTypes>>;
  setDemoState: Dispatch<SetStateAction<DemoState>>;
  setStreamingState: Dispatch<SetStateAction<StreamingState>>;
  setTurntableState: Dispatch<SetStateAction<TurntableState>>;
  checkSessionAvailability: (modelId?: string) => Promise<Response>;
};

const DEFAULT_CAMERA_VIEWS = {
  id: "",
  label: "",
  default: false,
  camId: "",
  cameraData: null,
};

const DEFAULT_DEMO_STATE = {
  camera: {
    name: "Cam_orbit_01main",
    id: "j6y250qj9vrenxw",
  },
  environment: {
    name: "ROOFTOP",
    id: ENV_TYPES.ROOFTOP_ID,
  },
  variants: [],
  sunState: SUN_STATE[MODELS["BE 6"].code].dayMode,
  rebounceCamera: false,
  selectedCameraView: DEFAULT_CAMERA_VIEWS,
  dropdownChangeSwitch: false,
  animationCommands: [`${ANIMATIONS[MODELS["BE 6"].code].goodByeHello.name}|seek|100`],
};

const DEFAULT_STREAMING_STATE = {
  switchStream: false,
  isStreamStarted: false,
  isStreamDisconnected: false,
  isStreamingAvailable: false,
  streamingNotAvailable: false,
  callStreamingAvailableAPI: false,
  isDontNotify: false,
  desktopMouseSensitivity: 1.5,
  mobileMoveSensitivity: 3,
};

const DEFAULT_TURNTABLE_STATE = {
  isTurntable: false,
  turntableIsEnabled: true,
  turntableCameraNamePrefix: CAMERA_PREFIX_NAMES.EXT,
  // selectedVariants: [],
};

export const StreamContext = createContext<StreamContextType>({
  loaded: false,
  loadError: false,
  cameras: [],
  scenarios: [],
  scenarioVariants: {},
  animations: [],
  streamViewType: DEFAULT_TURNTABLE_STATE.isTurntable
    ? STREAM_TYPES.TURNTABLE
    : STREAM_TYPES.STREAMING,
  demoState: DEFAULT_DEMO_STATE,
  streamingState: DEFAULT_STREAMING_STATE,
  turntableState: DEFAULT_TURNTABLE_STATE,
  /* eslint-disable */
  setDemoState: (state: DemoState | ((prev: DemoState) => DemoState)) => {},
  setStreamViewType: (state: StreamTypes | ((prev: StreamTypes) => StreamTypes)) => {},
  setStreamingState: (state: StreamingState | ((prev: StreamingState) => StreamingState)) => {},
  setTurntableState: (state: TurntableState | ((prev: TurntableState) => TurntableState)) => {},
  checkSessionAvailability: (modelId?: string) =>
    Promise.resolve(new Response(null, { status: 204 })),
  /* eslint-enable */
});

export type StreamProviderProps = {
  children?: React.ReactNode;
};

export function StreamProvider({ children }: StreamProviderProps) {
  const [demoState, setDemoState] = useState<DemoState>(DEFAULT_DEMO_STATE);
  const [streamingState, setStreamingState] = useState<StreamingState>(DEFAULT_STREAMING_STATE);
  const [turntableState, setTurntableState] = useState<TurntableState>(DEFAULT_TURNTABLE_STATE);
  const [streamViewType, setStreamViewType] = useState<StreamTypes>(
    DEFAULT_TURNTABLE_STATE.isTurntable ? STREAM_TYPES.TURNTABLE : STREAM_TYPES.STREAMING,
  );

  const model = useModel();
  const { loaded: configLoaded } = useConfigurationLoading();

  const {
    data: cameras,
    isSuccess: camerasLoaded,
    isError: camerasError,
  } = useQuery({
    queryKey: [`cameras-${model?.id}`, model?.id],
    queryFn: () =>
      fetchDataFile<apimodel_Paginated_apimodel_Camera>(
        `${process.env.REACT_APP_APEX_BASE_URL}/modelyears/${model?.id}/cameras`,
      ),
    onSuccess(data) {
      const defaultMainCamera =
        data?.items
          ?.filter((_cam) => EXTERIOR_CAMERA_NAMES.includes(_cam.name))
          ?.map((_cam, index) => ({
            id: `INT_Cam_${index + 1}`,
            label: EXTERIOR_CAMERA_LABELS[index],
            default: index === 0,
            camId: _cam.id,
            cameraData: _cam,
          }))
          .find((_cam) => _cam.default) || null;

      setDemoState((prev) => ({
        ...prev,
        selectedCameraView: defaultMainCamera,
      }));
    },
    enabled: configLoaded && Boolean(model?.id),
  });

  const {
    data: scenarios,
    isSuccess: scenariosLoaded,
    isError: scenariosError,
  } = useQuery({
    queryKey: [`scenarios-${model?.id}`, model?.id],
    queryFn: () =>
      fetchDataFile<apimodel_Paginated_apimodel_Scenario>(
        `${process.env.REACT_APP_APEX_BASE_URL}/modelyears/v2/${model?.id}/scenarios`,
      ),
    enabled: configLoaded && Boolean(model?.id),
  });

  const {
    data: scenarioVariants,
    isSuccess: scenarioVariantsLoaded,
    isError: scenarioVariantsError,
  } = useQuery({
    queryKey: [`scenario-variants-${model?.id}`, model?.id],
    queryFn: () =>
      fetchDataFile<ApiModelVariantSet>(
        `${process.env.REACT_APP_APEX_BASE_URL}/modelyears/${model?.id}/variantsets?include-variants=true`,
      ),
    onSuccess(data) {
      const firstVariantNames = data?.items?.sections[0]?.map(
        (item: Section) => item.variants?.[0]?.name,
      );

      setDemoState((prev) => ({
        ...prev,
        variants: firstVariantNames,
      }));
    },
    enabled: configLoaded && Boolean(model?.id),
  });

  const { data: animations, isSuccess: animationsLoaded } = useQuery({
    queryKey: [`animations-${model?.id}`, model?.id],
    queryFn: () =>
      fetchDataFile<[]>(
        `${process.env.REACT_APP_APEX_BASE_URL}/modelyears/${model?.id}/animations`,
      ),
    enabled: configLoaded && Boolean(model?.id),
  });

  /**
   * To check if streaming session is available or not
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: sessionData } = useQuery({
    queryKey: ["session"],
    enabled:
      streamingState.callStreamingAvailableAPI &&
      turntableState?.isTurntable &&
      !streamingState?.isDontNotify &&
      !streamingState?.isStreamingAvailable &&
      configLoaded,
    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_APEX_BASE_URL_V2}/streaming/modelyears/${model?.id}/available`,
      );
      return response;
    },
    onSuccess(data) {
      setStreamingState((prev) => ({
        ...prev,
        isStreamingAvailable: data?.status === 200,
        // isTurntable: data?.status !== 200,
      }));
    },
    onError() {
      setStreamingState((prev) => ({
        ...prev,
        isStreamingAvailable: false,
      }));
      setTurntableState((prev) => ({
        ...prev,
        isTurntable: true,
      }));
    },
    refetchInterval:
      !streamingState.isDontNotify &&
      turntableState?.isTurntable &&
      !streamingState?.isStreamingAvailable &&
      10000,
  });

  /**
   * API to check whether streaming session is available or not
   */
  const checkSessionAvailability = async (modelId?: string) => {
    const response = await fetch(
      `${process.env.REACT_APP_APEX_BASE_URL_V2}/streaming/modelyears/${modelId ? modelId : model?.id}/available`,
    );
    return response;
  };

  return (
    <StreamContext.Provider
      value={{
        loaded: camerasLoaded && scenariosLoaded && scenarioVariantsLoaded,
        loadError: camerasError || scenariosError || scenarioVariantsError,
        cameras: camerasLoaded ? cameras?.items : [],
        scenarios: scenariosLoaded ? scenarios?.items : [],
        animations: animationsLoaded ? animations : [],
        scenarioVariants: scenarioVariantsLoaded ? scenarioVariants : [],
        streamViewType,
        demoState,
        streamingState,
        turntableState,
        setStreamViewType,
        setDemoState,
        setStreamingState,
        setTurntableState,
        checkSessionAvailability,
      }}
    >
      {children}
    </StreamContext.Provider>
  );
}
